import type { ModalityType } from '../types';

/**
 * MED-1181 — the single source of truth for the per-cohort half of the Live
 * Session Delivery (LSD / MED02) gate.
 *
 * LSD is governed by TWO switches and every LSD affordance must respect both:
 *   1. the global `live_session_delivery` feature flag (read via
 *      `useFeatureFlag('live_session_delivery')`), and
 *   2. the cohort's **group-coaching** modality `live_session_delivery_enabled`.
 *
 * Before this helper existed, every component re-derived switch #2 by hand
 * (`modalities.find(m => m.modality_type === 'group_coaching')?.live_session_delivery_enabled`).
 * That duplication is what let the gate drift: when the global flag flipped ON
 * at GA, every site that only checked the flag lit up for classic MED01 cohorts.
 * Route ALL per-cohort LSD gating through this function so there is one place to
 * get the rule right.
 *
 * The combined pattern at a render site is:
 *   `flag && isLiveSessionDeliveryEnabled(cohort)`
 *
 * **Scope:** LSD is group-coaching-scoped — workshop and 1:1 modalities are
 * untouched by MED02, so this only ever inspects the `group_coaching` modality.
 *
 * **Fails closed:** returns `false` for a missing cohort, a cohort with no
 * `modalities` array, or a cohort without an active group-coaching modality.
 * This mirrors the backend `liveSessionDeliveryExpr` /
 * `COALESCE(gc.live_session_delivery_enabled, false)`.
 *
 * Sites whose data is a flat API payload (coach dashboard/cohort-detail rows,
 * participant per-session payloads) receive `live_session_delivery_enabled`
 * directly from the server and gate on that value — they do not carry a
 * `modalities` array, so they do not use this selector.
 */

/** Minimal structural shape this selector needs from a modality record. */
export interface LiveSessionDeliveryModalityLike {
  modality_type: ModalityType;
  live_session_delivery_enabled?: boolean | null;
}

/** Minimal structural shape this selector needs from a cohort object. */
export interface LiveSessionDeliveryCohortLike {
  modalities?: LiveSessionDeliveryModalityLike[] | null;
}

/**
 * Resolves the cohort's group-coaching modality and returns its
 * `live_session_delivery_enabled` value, defaulting to `false`.
 */
export function isLiveSessionDeliveryEnabled(
  cohort: LiveSessionDeliveryCohortLike | null | undefined,
): boolean {
  const groupCoaching = cohort?.modalities?.find(
    (m) => m.modality_type === 'group_coaching',
  );
  return groupCoaching?.live_session_delivery_enabled ?? false;
}

/** Minimal structural shape the peer-led selector needs from a modality. */
export interface PeerLedModalityLike {
  modality_type: ModalityType;
  coaching_sub_type?: 'coach_led' | 'peer_led' | null;
}

/** Minimal structural shape the peer-led selector needs from a cohort. */
export interface PeerLedCohortLike {
  modalities?: PeerLedModalityLike[] | null;
}

/**
 * MED-1202 — the single source of truth for "this group-coaching cohort has no
 * coaches" (peer-led).
 *
 * This is a DIFFERENT concept from Live Session Delivery and MUST NOT be
 * conflated with it:
 *
 *   • **Peer-led (this selector)** is chosen at cohort setup via the group
 *     coaching modality's `coaching_sub_type`. It answers "does this cohort
 *     have coaches?" — a peer-led cohort never does, whether or not sessions
 *     are delivered live. This is decided by `coaching_sub_type` ALONE.
 *   • **Live Session Delivery (`isLiveSessionDeliveryEnabled` + the global
 *     `live_session_delivery` flag)** gates the MED02 peer-leader live/prep
 *     experience. That is orthogonal to "has coaches".
 *
 * MED-1161 originally hid coach-management surfaces in the Groups view only
 * when the cohort was peer-led AND LSD was on (AC10: "with the flag off,
 * nothing changes"), and MED-1181 hardened that LSD condition. But a peer-led
 * cohort with LSD off is a valid, reachable configuration, and in that state
 * those coach surfaces wrongly reappeared (MED-1202). The backend already
 * models peer-led coach suppression on `coaching_sub_type` alone
 * (`SessionRepository.is_peer_led`, `GroupRepository.suppressPeerLedCoach`), as
 * do `CohortDetailPage`'s Coaches→Session-Leaders tab swap and the by-session
 * Leader column. Route coach-management *visibility* through THIS selector so
 * it follows the sub-type; keep the MED02 live/prep experience gated on
 * `flag && isLiveSessionDeliveryEnabled(cohort)`.
 *
 * **Fails closed:** returns `false` for a missing cohort, a cohort with no
 * `modalities` array, or one without a peer-led group-coaching modality.
 */
export function isPeerLedGroupCoaching(
  cohort: PeerLedCohortLike | null | undefined,
): boolean {
  return (
    cohort?.modalities?.some(
      (m) => m.modality_type === 'group_coaching' && m.coaching_sub_type === 'peer_led',
    ) ?? false
  );
}
